<script setup lang="ts">
import { WidthVariants } from '~/types/Content'

type Config = {
  type: string
  isContent: boolean
  isMedia: boolean
  order: 1 | 2
  background: string
}
type Panel = {
  fields: {}
  metadata: {}
  sys: {}
  config: Config // added by ColumnPanel component
}
type ColumnPanelSupportingData = {
  home: {
    removeBottomMargin?: {
      value: boolean
    }
    invertForMobile?: {
      value: boolean
    }
  }
}
type ColumnPanelProps = {
  title?: string
  panels: Panel[]
  width?: WidthVariants
  supportingData?: ColumnPanelSupportingData
}
const props = withDefaults(defineProps<ColumnPanelProps>(), { width: 'narrow' })
const getPanelComponent = (panel: any) => panel?.sys?.contentType?.sys?.id
const getComponentAttrs = (panel: any) =>
  ['richTextPanel', 'utilityPanel', 'accordionPanel', 'productCarousel', 'recommender'].includes(panel.config.type)
    ? { ...panel?.fields, id: panel?.sys?.id }
    : panel
const preset = props.width === 'wide' ? 'column-panel-wide' : 'column-panel'
const isNoMarginBottom = props?.supportingData?.home?.removeBottomMargin?.value
const invertForMobile = props?.supportingData?.home?.invertForMobile?.value === true

function getPanelConfig(panel: any) {
  const type = getPanelComponent(panel)
  const isContent = ['richTextPanel', 'accordionPanel'].includes(type) // TODO: Add more types
  const isMedia = ['picture', 'video'].includes(type)
  const order = 1 // default order
  /* Reading BG color from child Richtext, to be applied to the entire column. */
  const background = isContent && panel?.fields?.supportingData?.home?.backgroundColourForRichTextPanel?.value
  return { type, isContent, isMedia, order, background }
}

const publishedPanels = filterPublished(props.panels)

const defaultVal = { config: { isContent: false, isMedia: false, order: 1, background: '' } }
let [panel1 = defaultVal, panel2 = defaultVal] = publishedPanels
panel1.config = getPanelConfig(panel1)
panel2.config = getPanelConfig(panel2)

let firstPanel = panel1
let secondPanel = panel2
if (invertForMobile) {
  //after mobile inversion desktop order remains unchanged as implemented
  firstPanel = panel2
  secondPanel = panel1
  secondPanel.config.order = 1
  firstPanel.config.order = 2
}

const widthClass = props.width === 'narrow' || props.width === '' ? 'container-custom--narrow' : ''
</script>
<template>
  <section
    class="column-panel"
    data-testid="column-panel"
    :class="{
      'column-panel--no-margin-bottom': isNoMarginBottom,
    }"
  >
    <div class="container-custom" :class="widthClass">
      <div
        class="column-panel__column"
        :class="{
          'column-panel__column__content': firstPanel.config.isContent,
          'column-panel__column__media': firstPanel.config.isMedia,
        }"
        :style="{ order: firstPanel.config.order, background: firstPanel.config.background }"
      >
        <PanelSwitcher
          v-bind="{ ...getComponentAttrs(firstPanel), preset }"
          :component-name="getPanelComponent(firstPanel)"
        />
      </div>
      <div
        class="column-panel__column"
        :class="{
          'column-panel__column__content': secondPanel.config.isContent,
          'column-panel__column__media': secondPanel.config.isMedia,
        }"
        :style="{ order: secondPanel.config.order, background: secondPanel.config.background }"
      >
        <PanelSwitcher
          v-bind="{ ...getComponentAttrs(secondPanel), preset }"
          :component-name="getPanelComponent(secondPanel)"
        />
      </div>
    </div>
  </section>
</template>

<style lang="postcss">
.column-panel {
  margin-bottom: var(--component-space);
  overflow: hidden;

  &.column-panel--no-margin-bottom {
    margin-bottom: 0;
  }

  .column-panel__column {
    width: 100%;
    max-height: 75rem; /* 1200px */
    overflow: hidden;

    > .rich-text-panel {
      margin-bottom: 0; /* Override --component-space of components when used within ColumnPanel */
    }

    > .utility-panel--grid-links {
      margin-bottom: var(--space-90); /* Override --component-space of components when used within ColumnPanel */
      .container-custom {
        padding-inline: var(--space-70);
      }
    }
  }

  .column-panel__column__content {
    padding: var(--space-50) var(--space-50) var(--space-100) var(--space-50);
    display: flex;
    align-items: center;
    align-self: stretch;
    justify-content: center;
    width: 100%;

    > .rich-text-panel {
      max-width: var(--text-max-width); /* 416px */

      .container {
        padding-inline: 0;
      }
    }
  }

  .column-panel__column__media {
    img {
      object-fit: cover;
      display: block;
    }
    video {
      aspect-ratio: var(--aspect-ratio-5-4);
      object-fit: cover;
    }
  }

  .container-custom--narrow {
    margin: 0 auto;
    max-width: 78rem; /* 1280px - 1rem inline-padding */
  }

  @media (--tablet) {
    > .container-custom {
      display: flex; /* important to trigger the inline 'order' style */
    }

    .column-panel__column {
      width: 50%;
      overflow: hidden;

      > .utility-panel--grid-links {
        margin-bottom: 0;
      }
    }

    .column-panel__column__content {
      padding: var(--space-70);
    }
  }

  @media (--desktop) {
    .column-panel__column__content {
      padding-block: var(--space-100);
    }
  }
}
</style>
